import React from 'react'
import { Stack } from '@mui/material'

import type { HolidaySection as HolidaySectionData } from '@/src/types/holidays'
import type { RefundBand } from '@/src/utils/holidays/parseRefundTimeline'
import { parsePaymentSchedule } from '@/src/utils/holidays/parsePaymentSchedule'
import { parseStayOptions } from '@/src/utils/holidays/parseStayOptions'

import ItineraryTimelineWrapper from '../itineraryTimeline'
import HolidayListWrapper from '../holidayList'
import HolidayTableWrapper from '../holidayTable'
import HolidayParagraphsWrapper from '../holidayParagraphs'
import HolidayFaqWrapper from '../holidayFaq'
import PackageGridWrapper from '../packageGrid'
import RefundTimelineWrapper from '../refundTimeline'
import PaymentScheduleWrapper from '../paymentSchedule'
import DocumentChecklistWrapper from '../documentChecklist'
import StayOptionsWrapper from '../stayOptions'

import HolidaySection from './holidaySection'

interface HolidaySectionWrapperProps {
    section: HolidaySectionData
    isMobile: boolean
    variant?: 'card' | 'plain' | 'flat'
    /** `flat` only: the first section in a shared container draws no leading rule. */
    isFirst?: boolean
    /** Needed only so a `packageGrid` can build its card links. */
    collectionSlug?: string
    /**
     * Refund schedule for this section, when the page resolved one to it. Chosen at page level
     * because precedence depends on the whole document, not on any one section.
     */
    refundBands?: RefundBand[]
    /** `true` when the bands ARE this section's content, `false` when they ride on its footnote. */
    refundReplacesBody?: boolean
}

const VISA_SECTION = /visa/i
const PAYMENT_SECTION = /^payment-(schedule|terms)$/
const FLIGHT_SECTION = /^flight-details$/
const STAY_SECTION = /^stay-options$/
/** A visa section that groups its items is richer than a flat checklist; leave it to the list. */
const hasGroups = (section: { groups?: unknown[] }) => Boolean(section.groups?.length)

/**
 * The section registry. Every `HolidaySection.type` resolves to exactly one body here.
 *
 * A few sections also carry content the API only ever sends as prose — a refund schedule, a
 * payment schedule, a list of documents. Where that content parses into something structured the
 * body is drawn as a graphic instead; where it does not, the original wording still renders. The
 * decision is always driven by the content, never by the slug alone.
 */
const renderBody = (
    section: Exclude<HolidaySectionData, { type: 'packageGrid' }>,
    isMobile: boolean,
    refundBands?: RefundBand[],
    refundReplacesBody?: boolean
) => {
    switch (section.type) {
        case 'itinerary':
            return <ItineraryTimelineWrapper section={section} isMobile={isMobile} />
        case 'bulletList': {
            const timeline = refundBands?.length ? (
                <RefundTimelineWrapper bands={refundBands} isMobile={isMobile} />
            ) : null

            // Replacing is only correct when the section's own items are the schedule. A footnote
            // host keeps its content and gains the graphic alongside it.
            if (timeline && refundReplacesBody) return timeline

            const body =
                VISA_SECTION.test(section.id) && !hasGroups(section) ? (
                    <DocumentChecklistWrapper section={section} isMobile={isMobile} />
                ) : (
                    <HolidayListWrapper section={section} isMobile={isMobile} />
                )

            if (!timeline) return body

            // The graphic leads. It is the answer to the question the surrounding prose is
            // answering, and several paragraphs of terms above it meant it was only found by
            // whoever read to the end.
            return (
                <Stack spacing={3}>
                    {timeline}
                    {body}
                </Stack>
            )
        }
        case 'table': {
            // Decided from the parsed cells, not from the element: a component always returns a
            // truthy element, so branching on it would never reach the table. The id gate keeps an
            // unrelated table whose cells happen to end in brackets from becoming a stepper.
            const steps = PAYMENT_SECTION.test(section.id) ? parsePaymentSchedule(section) : null
            if (steps) return <PaymentScheduleWrapper steps={steps} isMobile={isMobile} />

            const options = STAY_SECTION.test(section.id) ? parseStayOptions(section) : null
            if (options) {
                return <StayOptionsWrapper options={options} caption={section.caption} isMobile={isMobile} />
            }

            // Only the flight table carries flight numbers, so only it looks up carrier logos.
            return (
                <HolidayTableWrapper
                    section={section}
                    withAirlineLogos={FLIGHT_SECTION.test(section.id)}
                    isMobile={isMobile}
                />
            )
        }
        case 'richText':
            return <HolidayParagraphsWrapper section={section} isMobile={isMobile} />
        case 'faq':
            return <HolidayFaqWrapper section={section} isMobile={isMobile} />
        default: {
            const unhandled: never = section
            return unhandled
        }
    }
}

const HolidaySectionWrapper: React.FC<HolidaySectionWrapperProps> = ({
    section,
    isMobile,
    variant = 'card',
    isFirst = false,
    collectionSlug = '',
    refundBands,
    refundReplacesBody
}) => {
    // The grid draws its own header so its sort control can share state with the cards.
    if (section.type === 'packageGrid') {
        return (
            <PackageGridWrapper
                section={section}
                collectionSlug={collectionSlug}
                isMobile={isMobile}
                variant={variant}
                isFirst={isFirst}
            />
        )
    }

    const body = renderBody(section, isMobile, refundBands, refundReplacesBody)
    if (!body) return null

    return (
        <HolidaySection
            id={section.id}
            // The itinerary titles itself with the package name, which the page header already
            // carries; its day rail supplies the heading in its place.
            title={section.type === 'itinerary' ? undefined : section.title}
            isMobile={isMobile}
            variant={variant}
            isFirst={isFirst}
        >
            {body}
        </HolidaySection>
    )
}

export { HolidaySectionWrapper }
export default HolidaySectionWrapper
